<template>

    <!-- Export Buttons -->
    <div class="mb-3 d-flex gap-2">
        <button class="btn btn-primary btn-sm" @click="exportJSON">Export JSON</button>
        <button class="btn btn-success btn-sm" @click="exportCSV">Export CSV</button>
        <button class="btn btn-warning btn-sm" @click="exportExcel">Export Excel</button>
    </div>
    <Card1 colClass="col-sm-12" cardhaderClass="pb-0 pb-0 card-no-border" pre="f-m-light mt-1" :title="title"
        span="true" :spanClass="desc">
        <!-- Rows Search -->
        <div class="table_search mb-3 d-flex justify-content-end align-items-center">
            <div class="d-flex gap-2 align-items-center ms-auto">
                <label for="table-complete-search" class="col-form-label">Search:</label>
                <input id="table-complete-search" type="text" class="form-control form-control-sm"
                    v-model="filterQuery" />
            </div>
        </div>

        <div class="table-responsive data-table theme-scrollbar zero-configuration">
            <table class="display table dataTable no-footer table-bordered fix-wrap" id="basic-1">
                <thead class="table-dark">
                    <tr>
                        <th scope="col" style="width:70px">Sr No</th>
                        <th v-for="(column, index) in columns" :key="index" :sortable="column.sortable"
                            @click="sortTable(column.field)" :style="{
                                cursor: column.sortable ? 'pointer' : 'default',
                                width: column.width || 'auto'
                            }">
                            {{ column.name }}
                        </th>
                        <th v-if="actions[0]" style="width:260px;" class="col-actions">Action</th>
                    </tr>
                </thead>

                <tbody v-if="!get_rows().length">
                    <tr>
                        <td valign="top" :colspan="columns.length + (actions[0] ? 2 : 1)" class="dataTables_empty">
                            No matching records found
                        </td>
                    </tr>
                </tbody>

                <tbody v-else>
                    <tr v-for="(row, index) in get_rows()" :key="index">
                        <td>{{ index + 1 }}</td>
                        <td v-for="(column, colIndex) in columns" :key="colIndex" class="wrap-cell">
                            <template v-if="column.field === 'icon'">
                                <img v-if="typeof row[column.field] === 'string'" :src="row[column.field]" alt="icon"
                                    style="width:30px;height:30px;object-fit:cover;border-radius:4px" />
                                <FontAwesomeIcon v-else-if="row[column.field]?.iconName && row[column.field]?.prefix"
                                    :icon="[row[column.field].prefix, row[column.field].iconName]"
                                    class="fs-4 text-dark" />
                                <span v-else class="text-muted">—</span>
                            </template>
                            <template v-else>
                                {{ row[column.field] }}
                            </template>
                        </td>

                        <td v-if="actions[0]" class="col-actions border-0">
                            <div class="action_btns d-flex gap-2 justify-content-cneter">
                                <button
                                    class="btn btn-sm btn-outline-primary"
                                    style="cursor:pointer"
                                    @click="viewRow(row)"
                                    :disabled="permissionKey && !canView(permissionKey)"
                                >
                                    <i class="icofont icofont-eye-alt"></i>
                                </button>
                                <button
                                    class="btn btn-sm btn-outline-success"
                                    style="cursor:pointer"
                                    @click="editRow(row)"
                                    :disabled="permissionKey && !canUpdate(permissionKey)"
                                >
                                    <i class="icofont icofont-edit"></i>
                                </button>
                                <button
                                    class="btn btn-sm btn-outline-danger"
                                    style="cursor:pointer"
                                    @click="deleteRow(row._id)"
                                    :disabled="permissionKey && !canDelete(permissionKey)"
                                >
                                    <i class="fas fa-trash"></i>
                                </button>

                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>


            <!-- Table bottom pagination, number of rows per page -->
            <div class="tabler_footer d-flex gap-3 justify-content-end align-items-center mt-2">
                <div class=" d-flex align-items-center  gap-2 align-items-center">
                    <label class="text-nowrap">Rows per page:</label>
                    <select v-model="elementsPerPage" class="form-select outline-none">
                        <option v-for="n in [10, 25, 50, 100]" :key="n" :value="n">{{ n }}</option>
                    </select>
                </div>
                <!-- Pagination -->
                <ul class="pagination justify-content-end pagination-primary">
                    <li class="page-item" :style="{ cursor: 'pointer' }">
                        <a class="page-link" @click="prev()" aria-label="Previous"><span
                                aria-hidden="true">&laquo;</span></a>
                    </li>
                    <li class="page-item" :style="{ cursor: 'pointer' }" v-for="i in num_pages()" :key="i"
                        v-bind:class="[i == currentPage ? 'active' : '']" v-on:click="change_page(i)">
                        <a class="page-link">{{ i }}</a>
                    </li>
                    <li class="page-item" :style="{ cursor: 'pointer' }">
                        <a class="page-link" @click="change()" aria-label="Next"><span
                                aria-hidden="true">&raquo;</span></a>
                    </li>
                </ul>
            </div>



        </div>
    </Card1>
</template>

<script lang="ts" setup>
import { ref, onMounted, watchEffect, defineAsyncComponent, defineProps, defineEmits } from "vue";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { usePermissions } from "@/composables/usePermissions";

const { canView, canUpdate, canDelete } = usePermissions();

const Card1 = defineAsyncComponent(() => import("@/components/commen/card/cardData1.vue"));
const emit = defineEmits(["isActive", "edit", "delete", "view"]);

let title = ref("");
const elementsPerPage = ref<number>(10); // default rows per page
const currentPage = ref<number>(1);
const filterQuery = ref<string>("");
const allData = ref([]);
const props = defineProps(["tableObj", "title", "permissionKey"]);

const sortOrder = ref("asc");
const sortedColumn = ref(null);
const columns = ref([]);
const actions = ref([]);

onMounted(() => {
    allData.value = props.tableObj?.data;
    columns.value = props.tableObj.columns;
    actions.value = props.tableObj.actions ? props.tableObj.actions : [];
    title.value = props.title;
});

watchEffect(() => {
    const source = props.tableObj?.data || [];
    const query = filterQuery.value.toLowerCase();
    if (query) {
        allData.value = source.filter((row) =>
            Object.values(row).some((val) =>
                val?.toString().toLowerCase().includes(query)
            )
        );
    } else {
        allData.value = source;
    }
});

// Pagination
function get_rows() {
    const start = (currentPage.value - 1) * elementsPerPage.value;
    const end = start + elementsPerPage.value;
    return allData.value.slice(start, end);
}
function num_pages() {
    return Math.ceil(allData.value.length / elementsPerPage.value);
}
function change_page(page: number) { currentPage.value = page; }
function change() { if (currentPage.value < num_pages()) currentPage.value++; }
function prev() { if (currentPage.value > 1) currentPage.value--; }

// Sorting
function sortTable(column: string) {
    if (sortedColumn.value === column) sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
    else sortedColumn.value = column, sortOrder.value = "asc";

    allData.value.sort((a, b) => {
        const aValue = a[column];
        const bValue = b[column];
        if (aValue < bValue) return sortOrder.value === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder.value === "asc" ? 1 : -1;
        return 0;
    });
}

// Row actions
function editRow(row) { emit("edit", row); }
function viewRow(row) { emit("view", row); }
function deleteRow(index) { emit("delete", index); }

// Export
function exportJSON() {
    const blob = new Blob([JSON.stringify(allData.value, null, 2)], { type: "application/json" });
    saveAs(blob, "table-data.json");
}
function exportCSV() {
    const ws = XLSX.utils.json_to_sheet(allData.value);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "table-data.csv");
}
function exportExcel() {
    const ws = XLSX.utils.json_to_sheet(allData.value);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "table-data.xlsx");
}
</script>

<style scoped>
.action_btns span {}

.table_search input {
    width: 300px;
}

.table,
.table th,
.table td,
tr {
    border: none !important;
}

.table tbody tr:nth-child(even) {
    background-color: #f9fafb;
    /* light gray */
}
</style>
